import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { LANGUAGE_EN } from '@home/_config/config';
import DonutChart from '@home/pages/studio/casualHub/components/DonutChart';
import {
  CasualCard,
  CasualCardHeader,
  CasualCardMetric,
  CasualCardTrend,
  CasualCardBody,
  getDodValues,
  isCasualHubMultiMonthRange,
} from '../CasualCard';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { ICasualTotalRevenueCardProps } from './type';
import './index.scss';

const CasualTotalRevenueCard: React.FC<ICasualTotalRevenueCardProps> = (props) => {
  const { config, apiDataKey, data: propsData, className, filter: filterProp } = props;
  const {
    titleEn,
    titleCn,
    metricKey,
    formatRule,
    preUnit,
    donutSegments,
    trendConfig,
    trendMetrics,
    trendTitleEn,
    trendTitleCn,
    trendTimeType,
    trendShowConfig,
    useChangeOnMultiMonth = true,
    compareMetrics,
    trendLastDays,
  } = config || {};

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const apiData = (propsData as Record<string, Record<string, unknown>>)?.[apiDataKey] || {};
  const { data, loading } = apiData as { data: Record<string, unknown>; loading: boolean; };
  const { trend_list: trendList, card_list: cardList } = (data || {}) as {
    trend_list: Record<string, unknown>[];
    card_list: Record<string, unknown>[];
  };

  const filterVal =
    filterProp ??
    ((propsData as Record<string, unknown> | undefined)?.filter as
      | Record<string, unknown>
      | undefined);

  const isMultiMonthRange = useMemo(
    () => isCasualHubMultiMonthRange(filterVal),
    [filterVal],
  );

  const cardData = cardList?.find(
    (card: Record<string, unknown>) => card?.metrics_name === metricKey
  ) as Record<string, unknown> | undefined;

  const segmentValues = useMemo(() => {
    if (!cardList || !donutSegments) return {};
    const result: Record<string, string | number> = {};
    donutSegments.forEach((seg) => {
      if (!seg.metricKey) return;
      const found = cardList.find(
        (card: Record<string, unknown>) => card?.metrics_name === seg.metricKey
      ) as Record<string, unknown> | undefined;
      if (found?.value !== undefined) {
        result[seg.metricKey] = found.value as string | number;
      }
    });
    return result;
  }, [cardList, donutSegments]);

  const computedDonutSegments = useMemo(() => {
    if (!donutSegments) return donutSegments;
    if (!cardData) {
      return donutSegments.map((seg) => Object.assign({}, seg, { value: 0 }));
    }
    const totalVal = parseFloat(cardData.value as string) || 0;
    if (totalVal <= 0) {
      return donutSegments.map((seg) => Object.assign({}, seg, { value: 0 }));
    }
    return donutSegments.map((seg) => {
      const segVal = parseFloat(segmentValues[seg.metricKey || ''] as string) || 0;
      const ratio = totalVal > 0 ? segVal / totalVal : 0;
      const clamped = Math.min(1, Math.max(0, ratio));
      /**
       * 极小 ratio（如 1e-18）来自浮点误差或接口尾差，numeral 格式化为 0.00% 时会得到 NaN；
       * 低于阈值时按 0 展示。
       */
      const displayRatio = clamped < 1e-10 ? 0 : clamped;
      const formatted = dataFormat({
        value: displayRatio,
        formatRule: 'absPercent2Decimal',
        fillZero: true,
      });
      let safeFormatted = formatted;
      if (typeof formatted === 'string' && formatted.includes('NaN')) {
        safeFormatted = dataFormat({
          value: 0,
          formatRule: 'absPercent2Decimal',
          fillZero: true,
        });
      }
      return Object.assign({}, seg, { value: safeFormatted });
    });
  }, [donutSegments, cardData, segmentValues]);

  const dodValues = getDodValues(cardData, {
    isMultiMonthRange: isMultiMonthRange && useChangeOnMultiMonth,
    compareMetrics,
  });

  const componentMetrics = useMemo(() => {
    if (trendMetrics?.length) {
      return trendMetrics.map(m => ({
        metricKey: m.metricKey,
        nameEn: m.nameEn,
        nameCn: m.nameCn,
        formatRule: m.formatRule || 'bmk00',
        color: m.color,
        isCompare: m.isCompare || false,
        compareToolTipNameEn: m.compareToolTipNameEn,
        compareToolTipNameCn: m.compareToolTipNameCn,
      }));
    }
    return [
      {
        metricKey: trendConfig?.metricKey || metricKey,
        nameEn: trendConfig?.nameEn || 'Revenue',
        nameCn: trendConfig?.nameCn || 'Revenue',
        formatRule: 'thousands2Decimal',
        isCompare: trendConfig?.isCompare || false,
        compareToolTipNameEn: trendConfig?.compareToolTipNameEn,
        compareToolTipNameCn: trendConfig?.compareToolTipNameCn,
      },
    ];
  }, [trendMetrics, trendConfig, metricKey]);

  return (
    <CasualCard className={className}>
      <Abnormal
        isLoading={loading !== false}
        isNoData={false}
        className="dbh-casual-card__abnormal"
        component={
          <>
            <CasualCardHeader titleEn={titleEn} titleCn={titleCn} />
            <CasualCardBody>
              <CasualCardMetric
                value={cardData?.value as string | number}
                formatRule={formatRule}
                preUnit={preUnit}
                dodData={dodValues}
              />

              <div className="dbh-casual-total-revenue-card__body">
                <div className="dbh-casual-total-revenue-card__segments">
                  {computedDonutSegments?.map((seg) => {
                    const segLabel = isEn ? seg.label : (seg.labelCn || seg.label);
                    return (
                      <div key={seg.label} className="dbh-casual-total-revenue-card__segment-group">
                        <div className="dbh-casual-total-revenue-card__segment-item">
                          <span
                            className="dbh-casual-total-revenue-card__segment-dot"
                            style={{ backgroundColor: seg.color }}
                          />
                          <span className="dbh-casual-total-revenue-card__segment-label">{segLabel}</span>
                        </div>
                        <span className="dbh-casual-total-revenue-card__segment-value">
                          {cardData ? `${seg.value}` : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <DonutChart
                  segments={computedDonutSegments}
                  size={72}
                  strokeWidth={14}
                  tooltipTitle={titleEn}
                  tooltipTitleCn={titleCn}
                  segmentValues={segmentValues}
                  preUnit={preUnit}
                  formatRule={formatRule}
                />
              </div>

              <CasualCardTrend
                trendTitleEn={trendTitleEn}
                trendTitleCn={trendTitleCn}
                trendList={trendLastDays && trendList?.length > trendLastDays ? trendList.slice(-trendLastDays) : trendList}
                componentMetrics={componentMetrics}
                trendShowConfig={trendShowConfig}
                trendTimeType={trendTimeType}
              />
            </CasualCardBody>
          </>
        }
      />
    </CasualCard>
  );
};

export default CasualTotalRevenueCard;
